import { useEffect } from 'react';
import { SITE_URL, HREFLANG, LANGS, OG_LOCALE, business } from './seoConfig';

const MARK = 'data-seo';

const upsert = (selector, create) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = create();
    el.setAttribute(MARK, '1');
    document.head.appendChild(el);
  }
  return el;
};

const setMeta = (attr, key, content) => {
  const el = upsert(`meta[${attr}="${key}"]`, () => {
    const m = document.createElement('meta');
    m.setAttribute(attr, key);
    return m;
  });
  el.setAttribute('content', content);
};

// Removes client-added AND build-time pre-rendered tags so each route carries exactly one set
const clearAll = (selector) => document.head.querySelectorAll(selector).forEach((n) => n.remove());

/**
 * Manages <head> tags per route. `alternates` maps lang -> path for hreflang;
 * `noindex` marks a page as non-indexable (e.g. 404).
 */
export const useSeo = ({ lang, path, title, description, alternates, jsonLd, noindex = false }) => {
  useEffect(() => {
    const url = `${SITE_URL}${path}`;
    document.documentElement.lang = lang;
    document.title = title;

    setMeta('name', 'description', description);
    setMeta('name', 'robots', noindex ? 'noindex, nofollow' : 'index, follow');
    setMeta('property', 'og:type', 'website');
    setMeta('property', 'og:site_name', business.name);
    setMeta('property', 'og:title', title);
    setMeta('property', 'og:description', description);
    setMeta('property', 'og:url', url);
    setMeta('property', 'og:image', business.logo);
    setMeta('property', 'og:locale', OG_LOCALE[lang]);
    setMeta('name', 'twitter:card', 'summary');
    setMeta('name', 'twitter:title', title);
    setMeta('name', 'twitter:description', description);
    setMeta('name', 'twitter:image', business.logo);

    clearAll('meta[property="og:locale:alternate"]');
    clearAll('link[rel="alternate"][hreflang]');
    clearAll('link[rel="canonical"]');
    clearAll('script[type="application/ld+json"]');

    if (!noindex) {
      const canonical = document.createElement('link');
      canonical.rel = 'canonical';
      canonical.href = url;
      canonical.setAttribute(MARK, '1');
      document.head.appendChild(canonical);

      if (alternates) {
        LANGS.forEach((l) => {
          if (!alternates[l]) return;
          const link = document.createElement('link');
          link.rel = 'alternate';
          link.hreflang = HREFLANG[l];
          link.href = `${SITE_URL}${alternates[l]}`;
          link.setAttribute(MARK, '1');
          document.head.appendChild(link);
          if (l !== lang) {
            const m = document.createElement('meta');
            m.setAttribute('property', 'og:locale:alternate');
            m.setAttribute('content', OG_LOCALE[l]);
            m.setAttribute(MARK, '1');
            document.head.appendChild(m);
          }
        });
        const xDefault = document.createElement('link');
        xDefault.rel = 'alternate';
        xDefault.hreflang = 'x-default';
        xDefault.href = alternates.xDefault ? `${SITE_URL}${alternates.xDefault}` : `${SITE_URL}/`;
        xDefault.setAttribute(MARK, '1');
        document.head.appendChild(xDefault);
      }
    }

    if (jsonLd) {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.text = JSON.stringify(jsonLd);
      script.setAttribute(MARK, '1');
      document.head.appendChild(script);
    }
  }, [lang, path, title, description, alternates, jsonLd, noindex]);
};
